import { useEffect, useState, useMemo, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { api, fmtErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Calendar } from "@/components/ui/calendar";
import { CalendarDays, Plus, Sparkles, MapPin, Trash2, User, Link as LinkIcon, Unlink, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

const SOURCE_LABEL = {
  airbnb: "Airbnb", vrbo: "Vrbo", booking: "Booking.com", direct: "Direct", other: "Other",
};

const dayKey = (d) => d.toISOString().slice(0, 10);
const parseDate = (s) => { const [y, m, d] = s.split("-").map(Number); return new Date(y, m - 1, d); };
const fmtNice = (s) => parseDate(s).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });

export default function HostSchedule() {
  const [bookings, setBookings] = useState([]);
  const [properties, setProperties] = useState([]);
  const [month, setMonth] = useState(new Date());
  const [openNew, setOpenNew] = useState(false);
  const [form, setForm] = useState(emptyForm());
  const [saving, setSaving] = useState(false);
  const [gcal, setGcal] = useState({ connected: false, configured: true });
  const [searchParams, setSearchParams] = useSearchParams();

  const load = useCallback(async () => {
    const [b, p, g] = await Promise.all([
      api.get("/bookings/mine"),
      api.get("/properties/mine"),
      api.get("/google/status").catch(() => ({ data: { connected: false, configured: false } })),
    ]);
    setBookings(b.data); setProperties(p.data); setGcal(g.data);
    // Auto-focus the calendar on the month of the next upcoming booking
    const today = new Date(); today.setHours(0,0,0,0);
    const nextUp = b.data.find((x) => parseDate(x.check_out) >= today);
    if (nextUp) setMonth(parseDate(nextUp.check_in));
  }, []);
  useEffect(() => { load(); }, [load]);

  // Handle OAuth redirect back from Google
  useEffect(() => {
    if (searchParams.get("gcal_connected") === "1") {
      toast.success("Google Calendar connected. Bookings will now sync automatically.");
      searchParams.delete("gcal_connected");
      setSearchParams(searchParams, { replace: true });
    } else if (searchParams.get("gcal_error")) {
      toast.error(`Google Calendar connect failed: ${searchParams.get("gcal_error")}`);
      searchParams.delete("gcal_error");
      setSearchParams(searchParams, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  const connectGoogle = async () => {
    try {
      const { data } = await api.get("/google/auth-url");
      window.location.href = data.authorization_url;
    } catch (e) { toast.error(fmtErr(e)); }
  };

  const disconnectGoogle = async () => {
    if (!window.confirm("Disconnect Google Calendar? Existing events will remain in your calendar but new bookings will no longer sync.")) return;
    try {
      await api.post("/google/disconnect");
      toast.success("Google Calendar disconnected");
      await load();
    } catch (e) { toast.error(fmtErr(e)); }
  };

  const { occupiedDays, turnoverDays } = useMemo(() => {
    const occ = new Set(); const turn = new Set();
    for (const b of bookings) {
      const start = parseDate(b.check_in); const end = parseDate(b.check_out);
      for (let d = new Date(start); d < end; d.setDate(d.getDate() + 1)) occ.add(dayKey(d));
      turn.add(b.check_out);
    }
    return {
      occupiedDays: Array.from(occ).map(parseDate),
      turnoverDays: Array.from(turn).map(parseDate),
    };
  }, [bookings]);

  const today = new Date(); today.setHours(0,0,0,0);
  const upcoming = bookings.filter((b) => parseDate(b.check_out) >= today).sort((a, b) => a.check_in.localeCompare(b.check_in));
  const past     = bookings.filter((b) => parseDate(b.check_out) <  today).sort((a, b) => b.check_in.localeCompare(a.check_in));

  const create = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = { ...form, guests: Number(form.guests || 1), cleaner_email: form.cleaner_email || null };
      await api.post("/bookings", payload);
      toast.success("Booking added. Cleaning team notified if you provided an email.");
      setOpenNew(false); setForm(emptyForm()); await load();
    } catch (e) { toast.error(fmtErr(e)); }
    finally { setSaving(false); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this booking?")) return;
    try { await api.delete(`/bookings/${id}`); toast.success("Booking removed"); await load(); }
    catch (e) { toast.error(fmtErr(e)); }
  };

  return (
    <div className="scent-wave min-h-[calc(100vh-4rem)]">
      <div className="max-w-6xl mx-auto px-6 md:px-10 py-10">
        <div className="flex items-end justify-between flex-wrap gap-4">
          <div>
            <div className="text-xs tracking-[0.28em] uppercase text-emerald-700 font-semibold">Host · Schedule</div>
            <h1 className="mt-2 font-serif text-4xl md:text-5xl tracking-tight text-[#0A1128]">Turnover calendar.</h1>
            <p className="mt-2 text-slate-600 max-w-xl">Every booking auto-creates an occupied block and flags check-out day as a turnover for your cleaning team.</p>
          </div>
          <Dialog open={openNew} onOpenChange={setOpenNew}>
            <DialogTrigger asChild>
              <Button data-testid="add-booking" disabled={properties.length === 0} className="rounded-full bg-[#0A1128] hover:bg-[#1A2A3A] text-white">
                <Plus className="w-4 h-4 mr-1" /> Add booking
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-lg">
              <DialogHeader><DialogTitle className="font-serif text-2xl">New booking</DialogTitle></DialogHeader>
              <form onSubmit={create} data-testid="booking-form" className="space-y-3 mt-2">
                <div>
                  <Label>Property</Label>
                  <Select value={form.property_id} onValueChange={(v)=>setForm({...form, property_id: v})}>
                    <SelectTrigger data-testid="bk-property"><SelectValue placeholder="Choose a property" /></SelectTrigger>
                    <SelectContent>
                      {properties.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div><Label>Guest name</Label><Input required value={form.guest_name} onChange={(e)=>setForm({...form, guest_name: e.target.value})} data-testid="bk-name" /></div>
                <div className="grid grid-cols-2 gap-3">
                  <div><Label>Check-in</Label><Input type="date" required value={form.check_in} onChange={(e)=>setForm({...form, check_in: e.target.value})} data-testid="bk-in" /></div>
                  <div><Label>Check-out</Label><Input type="date" required value={form.check_out} onChange={(e)=>setForm({...form, check_out: e.target.value})} data-testid="bk-out" /></div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div><Label>Guests</Label><Input type="number" min={1} value={form.guests} onChange={(e)=>setForm({...form, guests: e.target.value})} data-testid="bk-guests" /></div>
                  <div>
                    <Label>Source</Label>
                    <Select value={form.source} onValueChange={(v)=>setForm({...form, source: v})}>
                      <SelectTrigger data-testid="bk-source"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="direct">Direct</SelectItem>
                        <SelectItem value="airbnb">Airbnb</SelectItem>
                        <SelectItem value="vrbo">Vrbo</SelectItem>
                        <SelectItem value="booking">Booking.com</SelectItem>
                        <SelectItem value="other">Other</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div>
                  <Label>Cleaner email <span className="text-slate-400 text-xs">(optional — auto-notified on save)</span></Label>
                  <Input type="email" value={form.cleaner_email} onChange={(e)=>setForm({...form, cleaner_email: e.target.value})} data-testid="bk-cleaner"
                    placeholder="cleaner@example.com" />
                </div>
                <div><Label>Notes</Label><Textarea rows={2} value={form.notes} onChange={(e)=>setForm({...form, notes: e.target.value})} data-testid="bk-notes" /></div>
                <Button type="submit" disabled={saving || !form.property_id} data-testid="bk-submit" className="w-full rounded-full bg-[#0A1128] text-white hover:bg-[#1A2A3A]">
                  {saving ? "Saving…" : "Save booking"}
                </Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {properties.length === 0 && (
          <div className="mt-10 p-8 rounded-3xl border border-dashed border-slate-300 bg-white text-center text-slate-500" data-testid="schedule-no-property">
            Add a property first, then start recording bookings.
          </div>
        )}

        {/* Google Calendar sync banner */}
        {gcal.configured && (
          gcal.connected ? (
            <div className="mt-8 p-5 rounded-3xl bg-emerald-50 border border-emerald-200 flex items-center justify-between flex-wrap gap-4" data-testid="gcal-connected-banner">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-emerald-600 flex items-center justify-center">
                  <CheckCircle2 className="w-5 h-5 text-white" />
                </div>
                <div>
                  <div className="font-serif text-lg text-[#0A1128]">Google Calendar connected</div>
                  <div className="text-xs text-slate-600">
                    {gcal.email ? <>Syncing to <b>{gcal.email}</b></> : "Bookings sync automatically to your primary calendar."}
                  </div>
                </div>
              </div>
              <Button variant="outline" onClick={disconnectGoogle} data-testid="gcal-disconnect"
                className="rounded-full border-slate-300">
                <Unlink className="w-4 h-4 mr-1" /> Disconnect
              </Button>
            </div>
          ) : (
            <div className="mt-8 p-5 rounded-3xl bg-white border border-slate-200 flex items-center justify-between flex-wrap gap-4" data-testid="gcal-connect-banner">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-[#0A1128] flex items-center justify-center">
                  <CalendarDays className="w-5 h-5 text-[#D4AF37]" />
                </div>
                <div>
                  <div className="font-serif text-lg text-[#0A1128]">Sync turnovers to Google Calendar</div>
                  <div className="text-xs text-slate-600 max-w-md">One click connects your Google account. Every booking + turnover shows up on your phone and shared calendars automatically.</div>
                </div>
              </div>
              <Button onClick={connectGoogle} data-testid="gcal-connect"
                className="rounded-full bg-[#0A1128] hover:bg-[#1A2A3A] text-white">
                <LinkIcon className="w-4 h-4 mr-1" /> Connect Google Calendar
              </Button>
            </div>
          )
        )}

        {properties.length > 0 && (
          <div className="mt-10 grid md:grid-cols-5 gap-8">
            <div className="md:col-span-2">
              <div className="bg-white rounded-3xl border border-slate-200 p-4">
                <Calendar
                  mode="single"
                  month={month}
                  onMonthChange={setMonth}
                  modifiers={{ occupied: occupiedDays, turnover: turnoverDays }}
                  modifiersClassNames={{
                    occupied: "bg-[#0A1128] text-white rounded-md hover:bg-[#1A2A3A] hover:text-white",
                    turnover: "!bg-[#D4AF37] !text-[#0A1128] font-semibold rounded-md",
                  }}
                  className="rounded-md"
                />
                <div className="mt-4 flex items-center gap-4 text-xs text-slate-600 px-2 pb-1">
                  <span className="inline-flex items-center gap-2"><span className="w-3 h-3 rounded bg-[#0A1128]" /> Occupied</span>
                  <span className="inline-flex items-center gap-2"><span className="w-3 h-3 rounded bg-[#D4AF37]" /> Turnover / Cleaning</span>
                </div>
              </div>
            </div>

            <div className="md:col-span-3">
              <div className="text-[10px] tracking-[0.28em] uppercase text-emerald-700 font-semibold">Upcoming ({upcoming.length})</div>
              <div className="mt-3 space-y-3">
                {upcoming.length === 0 && (
                  <div className="p-6 rounded-2xl border border-dashed border-slate-300 bg-white text-slate-500 text-sm" data-testid="schedule-empty">
                    No upcoming bookings yet. Add your first one and the calendar lights up.
                  </div>
                )}
                {upcoming.map((b) => <BookingRow key={b.id} b={b} onDelete={() => remove(b.id)} />)}
              </div>

              {past.length > 0 && (
                <div className="mt-8">
                  <div className="text-[10px] tracking-[0.28em] uppercase text-slate-500 font-semibold">Past ({past.length})</div>
                  <div className="mt-3 space-y-3">
                    {past.slice(0, 5).map((b) => <BookingRow key={b.id} b={b} onDelete={() => remove(b.id)} muted />)}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function BookingRow({ b, onDelete, muted }) {
  return (
    <div className={`rounded-2xl border p-5 ${muted ? "bg-slate-50 border-slate-200 opacity-80" : "bg-white border-slate-200"}`} data-testid={`booking-${b.id}`}>
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-emerald-50 flex items-center justify-center">
            <CalendarDays className="w-5 h-5 text-emerald-600" />
          </div>
          <div>
            <div className="font-serif text-lg text-[#0A1128] leading-tight">{b.guest_name}</div>
            <div className="text-xs text-slate-500 flex items-center gap-1 mt-0.5"><MapPin className="w-3 h-3" /> {b.property_name}</div>
          </div>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          <span className="text-sm text-[#0A1128] font-medium">{fmtNice(b.check_in)} <span className="text-slate-400">→</span> {fmtNice(b.check_out)}</span>
          <span className="text-[10px] tracking-[0.2em] uppercase px-2 py-1 rounded-full bg-slate-100 text-slate-600">{SOURCE_LABEL[b.source] || b.source}</span>
          <span className="text-xs text-slate-500 inline-flex items-center gap-1"><User className="w-3 h-3" /> {b.guests}</span>
        </div>
      </div>
      <div className="mt-3 flex items-center justify-between flex-wrap gap-2">
        <div className="text-xs px-3 py-1.5 rounded-full bg-[#F9F3E5] border border-[#D4AF37] text-[#0A1128] inline-flex items-center gap-1.5">
          <Sparkles className="w-3 h-3" /> Turnover / cleaning on <b>{fmtNice(b.check_out)}</b>
          {b.cleaner_email && <span className="text-slate-500">· notified {b.cleaner_email}</span>}
        </div>
        <button onClick={onDelete} data-testid={`bk-delete-${b.id}`} className="text-slate-400 hover:text-red-600 text-xs inline-flex items-center gap-1">
          <Trash2 className="w-3.5 h-3.5" /> Remove
        </button>
      </div>
      {b.notes && <div className="mt-2 text-sm text-slate-600 whitespace-pre-wrap">{b.notes}</div>}
    </div>
  );
}

function emptyForm() {
  return { property_id: "", guest_name: "", check_in: "", check_out: "", guests: 1, source: "direct", notes: "", cleaner_email: "" };
}
